import type { ShapeStrokeStyle } from "@/pages/canvas/hooks/useCanvasShapeAndEdgeStyle";
import {
  normalizeCanvasStrokeWidth,
  normalizeTextLabelFontFamily,
  normalizeTextLabelFontSize,
  parseFontSizePx,
  TEXT_LABEL_PLACEHOLDER,
  type CanvasShapeNodeProps,
} from "@/pages/canvas/types/canvasNodeData";
import { cn } from "@/utils";
import { LinkNode } from "@lexical/link";
import { ListItemNode, ListNode } from "@lexical/list";
import {
  $convertFromMarkdownString,
  $convertToMarkdownString,
  BOLD_ITALIC_STAR,
  BOLD_ITALIC_UNDERSCORE,
  BOLD_STAR,
  BOLD_UNDERSCORE,
  ITALIC_STAR,
  ITALIC_UNDERSCORE,
  ORDERED_LIST,
  STRIKETHROUGH,
  UNORDERED_LIST,
} from "@lexical/markdown";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { MarkdownShortcutPlugin } from "@lexical/react/LexicalMarkdownShortcutPlugin";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { NodeResizer, useReactFlow } from "@xyflow/react";
import {
  $addUpdateTag,
  $setSelection,
  SKIP_DOM_SELECTION_TAG,
  type EditorState,
  type LexicalEditor,
} from "lexical";
import { AnimatePresence } from "motion/react";
import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { LATTICE, snapToGrid } from "../canvas-controls.config";
import { useCanvasApi } from "../context/CanvasContext";
import {
  useCanvasSelectionCount,
  useIsMultiSelectToolbarOpen,
} from "../context/CanvasSelectionCountContext";
import { useCanvasUi } from "../context/CanvasUiContext";
import { useCanvasShapeAndEdgeStyle } from "../hooks/useCanvasShapeAndEdgeStyle";
import { useSectionPreviewHighlight } from "../hooks/useSectionPreviewHighlight";
import { clearDomSelectionInside } from "../utils/domSelection";
import { ShapeFloatingToolbar } from "./ShapeFloatingToolbar";
import type { ShapeType } from "./shapes/index";
import { Shape } from "./shapes/Shape";
import {
  DEFAULT_LABEL_COLOR,
  DEFAULT_SHAPE_COLOR,
  SHAPE_COLORS,
} from "./shared/CanvasColorPalettes";
import { CanvasConnectHandles } from "./shared/CanvasConnectHandles";
import { CanvasFloatingToolbarPortal } from "./shared/CanvasFloatingToolbarPortal";
import { TextLabelFloatingToolbar } from "./TextLabelFloatingToolbar";

const TRANSFORMERS = [
  BOLD_STAR,
  BOLD_UNDERSCORE,
  ITALIC_STAR,
  ITALIC_UNDERSCORE,
  BOLD_ITALIC_STAR,
  BOLD_ITALIC_UNDERSCORE,
  STRIKETHROUGH,
  UNORDERED_LIST,
  ORDERED_LIST,
];

const textNodeTheme = {
  paragraph: "mb-0 text-center",
  list: {
    ul: "list-disc list-inside ml-2",
    ol: "list-decimal list-inside ml-2",
    listitem: "ml-2",
  },
  text: {
    bold: "font-bold",
    strikethrough: "line-through",
    code: "bg-n-95/10 dark:bg-n-95/10 px-1 py-0.5 rounded text-xs font-mono",
  },
  link: "text-teal dark:text-teal underline hover:no-underline cursor-pointer",
};

/**
 * The text box inside each shape: insets from the node's bounding box, as
 * percentages of its width (left/right) and height (top/bottom). Text wraps
 * to this box, centres in it, and is clipped by it — the shape's inner area,
 * not its bounding box, so a circle or diamond never runs text into its
 * corners. Values approximate each shape's inscribed rectangle.
 */
type TextInset = { top: number; right: number; bottom: number; left: number };

const SHAPE_TEXT_INSETS: Record<ShapeType, TextInset> = {
  rectangle: { top: 6, right: 6, bottom: 6, left: 6 },
  cylinder: { top: 22, right: 8, bottom: 16, left: 8 },
  circle: { top: 15, right: 15, bottom: 15, left: 15 },
  pentagon: { top: 22, right: 16, bottom: 12, left: 16 },
  diamond: { top: 25, right: 25, bottom: 25, left: 25 },
  triangle: { top: 40, right: 22, bottom: 8, left: 22 },
};

function textInsetStyle(shapeType: ShapeType): React.CSSProperties {
  const i = SHAPE_TEXT_INSETS[shapeType] ?? SHAPE_TEXT_INSETS.rectangle;
  return {
    top: `${i.top}%`,
    right: `${i.right}%`,
    bottom: `${i.bottom}%`,
    left: `${i.left}%`,
  };
}

/** Height of the text box for a node of `height`, in px. */
function textBoxHeight(shapeType: ShapeType, height: number): number {
  const i = SHAPE_TEXT_INSETS[shapeType] ?? SHAPE_TEXT_INSETS.rectangle;
  return (height * (100 - i.top - i.bottom)) / 100;
}

function serializeToMarkdown(editorState: EditorState): string {
  let markdown = "";
  editorState.read(() => {
    markdown = $convertToMarkdownString(TRANSFORMERS);
  });
  // Lexical serializes an empty paragraph as "\n" — normalize to ""
  // so content is reliably empty when the editor has no visible text.
  return markdown.trim();
}

function EditorRefBridge({
  editorRef,
}: {
  editorRef: React.MutableRefObject<LexicalEditor | null>;
}) {
  const [editor] = useLexicalComposerContext();
  useEffect(() => {
    editorRef.current = editor;
  }, [editor, editorRef]);
  return null;
}

function InitializerPlugin({
  value,
  isFocused,
  onInitialized,
}: {
  value: string;
  isFocused: boolean;
  onInitialized: () => void;
}) {
  const [editor] = useLexicalComposerContext();
  const lastValueRef = useRef<string>("");
  const hasInitializedRef = useRef(false);

  // Remote content lands while nobody is typing here. SKIP_DOM_SELECTION_TAG
  // keeps Lexical from reconciling the import's selection into the DOM of an
  // unfocused editor (WebKit paints that as a stray highlight), and the
  // selection is dropped so the next focus starts clean.
  const importRemote = useCallback(
    (markdown: string) => {
      const rootEl = editor.getRootElement();
      const focused = !!rootEl && rootEl.contains(document.activeElement);
      editor.update(() => {
        if (!focused) $addUpdateTag(SKIP_DOM_SELECTION_TAG);
        $convertFromMarkdownString(markdown, TRANSFORMERS);
        if (!focused) $setSelection(null);
      });
    },
    [editor],
  );

  useEffect(() => {
    if (!hasInitializedRef.current) {
      hasInitializedRef.current = true;
      lastValueRef.current = value;
      if (value) importRemote(value);
      onInitialized();
      return;
    }
    if (isFocused) {
      lastValueRef.current = value;
      return;
    }
    if (value === lastValueRef.current) return;
    lastValueRef.current = value;
    importRemote(value || "");
  }, [value, importRemote, isFocused, onInitialized]);

  return null;
}

export const ShapeNode = memo(function ShapeNode({
  id,
  data,
  selected,
  dragging,
  width = 150,
  height = 150,
  positionAbsoluteX,
  positionAbsoluteY,
}: CanvasShapeNodeProps) {
  const canvas = useCanvasApi();
  const { setToolbarPopoverOpen } = useCanvasUi();
  const { canEdit } = canvas;
  const { updateNode } = useReactFlow();
  const { setShapeStyle } = useCanvasShapeAndEdgeStyle();

  const shapeType: ShapeType = (data?.shape_type ?? "rectangle") as ShapeType;
  const fillColor = data?.fill_color ?? DEFAULT_SHAPE_COLOR;
  const strokeColor =
    data?.stroke_color ??
    SHAPE_COLORS.find((c) => c.value === fillColor)?.swatchHex ??
    "#3b82f6";
  const strokeStyle = (data?.stroke_style ?? "solid") as ShapeStrokeStyle;
  const strokeWidth = normalizeCanvasStrokeWidth(data?.stroke_width);
  const noFill = data?.no_fill ?? false;
  const content = data?.content ?? "";
  // Same family vocabulary as text labels (the schema enum).
  const fontFamily = normalizeTextLabelFontFamily(data?.font_family);
  // Same default and clamp as every other text surface (16px, "Medium").
  const fontSize = `${parseFontSizePx(data?.font_size)}px`;
  const isBold = data?.is_bold ?? false;
  const isItalic = data?.is_italic ?? false;
  const isUnderline = data?.is_underline ?? false;

  const strokeDasharray =
    strokeStyle === "none"
      ? undefined
      : strokeStyle === "dashed"
        ? "16 8"
        : strokeStyle === "dotted"
          ? "3 8"
          : undefined;
  const effectiveStrokeColor =
    strokeStyle === "none" ? "transparent" : strokeColor;
  const effectiveStrokeWidth = strokeStyle === "none" ? 0 : strokeWidth;

  // Text colour: the text-label palette and its Default. Default is ink — the
  // fixed sticky ink on a filled shape (its tint stays light in dark mode, as
  // on a note), the theme foreground on a no-fill shape (it sits on the
  // canvas ground, like a text label).
  const textColor = data?.text_color ?? DEFAULT_LABEL_COLOR;
  const effectiveTextColor =
    textColor === DEFAULT_LABEL_COLOR
      ? noFill
        ? "hsl(var(--foreground))"
        : "var(--sticky-foreground)"
      : textColor;

  const [isEditing, setIsEditing] = useState(selected && canEdit);
  const [isFocused, setIsFocused] = useState(false);
  // Lines that fit the text box at rest. The box is clamped to this many and
  // the last line ends in an ellipsis when there is more — the overflow
  // signal. Lifted while editing so the caret can reach every line.
  const [lineClamp, setLineClamp] = useState(1);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  const editorRef = useRef<LexicalEditor | null>(null);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const resizeOriginRef = useRef<{ x: number; y: number } | null>(null);
  const shouldAutoFocusRef = useRef(selected && canEdit);
  const wasSelectedOnMouseDownRef = useRef(false);
  const mouseDownPosRef = useRef<{ x: number; y: number } | null>(null);
  // Tracks whether the toolbar received a mousedown — used to suppress blur
  // when the user clicks a toolbar button while the editor is focused.
  const isPopoverOpenRef = useRef(false);
  const toolbarMouseDownRef = useRef(false);

  // The text is contenteditable only for the edit session; the DOM attribute
  // follows editor.setEditable (ContentEditable ignores its own prop). A
  // layout effect so the attribute is in place before the rAF focus loop.
  // Leaving edit mode drops any range still anchored in the editor so WebKit
  // never paints it.
  useLayoutEffect(() => {
    editorRef.current?.setEditable(canEdit && isEditing);
    if (!isEditing) {
      clearDomSelectionInside(editorRef.current?.getRootElement());
    }
  }, [canEdit, isEditing]);

  // Auto-focus loop — identical to TextLabelNode
  useEffect(() => {
    if (!shouldAutoFocusRef.current) return;
    const rafId = requestAnimationFrame(() => {
      const rootEl = editorRef.current?.getRootElement();
      if (!rootEl) return;
      rootEl.focus({ preventScroll: true });
      if (
        document.activeElement === rootEl ||
        rootEl.contains(document.activeElement)
      ) {
        shouldAutoFocusRef.current = false;
      }
    });
    return () => cancelAnimationFrame(rafId);
  }); // intentionally no dep array

  useLayoutEffect(() => {
    const rootEl = editorRef.current?.getRootElement();
    const lineHeightPx = rootEl
      ? parseFloat(getComputedStyle(rootEl).lineHeight)
      : NaN;
    const fontPx = parseFontSizePx(fontSize);
    const lh = Number.isFinite(lineHeightPx) ? lineHeightPx : fontPx * 1.5;
    setLineClamp(
      Math.max(1, Math.floor(textBoxHeight(shapeType, height) / lh)),
    );
  }, [height, shapeType, fontSize, fontFamily, isInitialized]);

  const handleStartEditing = useCallback(() => {
    if (!canEdit) return;
    setIsEditing(true);
    shouldAutoFocusRef.current = true;
  }, [canEdit]);

  const handleContentChange = useCallback(
    (editorState: EditorState) => {
      if (!isInitialized) return;
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = setTimeout(() => {
        const markdown = serializeToMarkdown(editorState);
        canvas.updateNodeData(id, {
          content: markdown,
          updated_at: new Date().toISOString(),
        });
      }, 300);
    },
    [id, canvas, isInitialized],
  );

  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
  }, []);

  // Blur exits edit mode — but NOT when the user is clicking a toolbar button
  // (toolbarMouseDownRef) or when a color/style popover is open (isPopoverOpenRef).
  const handleBlur = useCallback(() => {
    if (toolbarMouseDownRef.current || isPopoverOpenRef.current) return;
    // Flush any pending debounced save immediately so content is in Loro
    // before isEditing → false. Without this, the brief window where
    // isEditing=false and content="" causes the "Add text" hint to flash.
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
      const editor = editorRef.current;
      if (editor) {
        const markdown = serializeToMarkdown(editor.getEditorState());
        canvas.updateNodeData(id, {
          content: markdown,
          updated_at: new Date().toISOString(),
        });
      }
    }
    setIsEditing(false);
  }, [id, canvas]);

  const handleFontFamilyChange = useCallback(
    (newFontFamily: string) => {
      canvas.updateNodeData(id, {
        font_family: normalizeTextLabelFontFamily(newFontFamily),
      });
    },
    [id, canvas],
  );

  const handleFontSizeChange = useCallback(
    (newFontSize: string) => {
      canvas.updateNodeData(id, {
        font_size: normalizeTextLabelFontSize(newFontSize),
      });
    },
    [id, canvas],
  );

  const handleTextColorChange = useCallback(
    (color: string) => {
      canvas.updateNodeData(id, {
        text_color: color === DEFAULT_LABEL_COLOR ? null : color,
      });
    },
    [id, canvas],
  );

  const handleBoldChange = useCallback(() => {
    canvas.updateNodeData(id, { is_bold: !isBold });
  }, [id, canvas, isBold]);

  const handleItalicChange = useCallback(() => {
    canvas.updateNodeData(id, { is_italic: !isItalic });
  }, [id, canvas, isItalic]);

  const handleUnderlineChange = useCallback(() => {
    canvas.updateNodeData(id, { is_underline: !isUnderline });
  }, [id, canvas, isUnderline]);

  const handleFillColorChange = useCallback(
    (color: string) => {
      const entry = SHAPE_COLORS.find((c) => c.value === color);
      canvas.updateNodeData(id, {
        fill_color: color,
        stroke_color: entry?.swatchHex ?? color,
      });
      setShapeStyle({ color });
    },
    [id, canvas, setShapeStyle],
  );

  const handleNoFillChange = useCallback(
    (nextNoFill: boolean) => {
      canvas.updateNodeData(id, { no_fill: nextNoFill });
      setShapeStyle({ noFill: nextNoFill });
    },
    [id, canvas, setShapeStyle],
  );

  const handleStrokeStyleChange = useCallback(
    (style: ShapeStrokeStyle) => {
      canvas.updateNodeData(id, { stroke_style: style });
      setShapeStyle({ strokeStyle: style });
    },
    [id, canvas, setShapeStyle],
  );

  const handleShapeTypeChange = useCallback(
    (nextShape: ShapeType) => {
      canvas.updateNodeData(id, { shape_type: nextShape });
    },
    [id, canvas],
  );

  const handlePopoverOpenChange = useCallback(
    (open: boolean) => {
      isPopoverOpenRef.current = open;
      setIsPopoverOpen(open);
      setToolbarPopoverOpen(open);

      if (!open) {
        // The editor blur fires before Radix calls onOpenChange(false), so
        // handleBlur saw isPopoverOpenRef=true and returned early. After one
        // tick all pending events have settled — if the editor still has no
        // focus, exit edit mode now to avoid a permanently stuck isEditing state.
        setTimeout(() => {
          if (isPopoverOpenRef.current || toolbarMouseDownRef.current) return;
          const editorEl = editorRef.current?.getRootElement();
          if (!editorEl?.contains(document.activeElement)) {
            setIsEditing(false);
          }
        }, 0);
      }
    },
    [setToolbarPopoverOpen],
  );

  const selectedNodeCount = useCanvasSelectionCount();
  const isMultiSelectToolbarOpen = useIsMultiSelectToolbarOpen();

  // Multi-select unmounts this toolbar without Radix onOpenChange(false).
  // Deselect must also clear the latch — otherwise isPopoverOpen alone would
  // keep the toolbar mounted after a canvas click.
  useEffect(() => {
    if (!isPopoverOpen) return;
    if (isMultiSelectToolbarOpen || !selected) {
      handlePopoverOpenChange(false);
    }
  }, [
    isMultiSelectToolbarOpen,
    selected,
    isPopoverOpen,
    handlePopoverOpenChange,
  ]);

  const isSectionPreviewMember = useSectionPreviewHighlight(id);

  // Toolbar follows selection only — do not keep it mounted via isPopoverOpen /
  // isEditing after deselection.
  // Suppress when MultiSelectFloatingToolbar is mounted (multiSelectNodes >= 2).
  const showToolbar =
    canEdit &&
    !dragging &&
    !isMultiSelectToolbarOpen &&
    selected &&
    selectedNodeCount === 1;

  const initialConfig = useMemo(
    () => ({
      namespace: `ShapeNode-${id}`,
      nodes: [ListNode, ListItemNode, LinkNode],
      onError: (error: Error) => {
        console.error("Lexical error in ShapeNode:", error);
      },
      theme: textNodeTheme,
      editable: canEdit && isEditing,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [id],
  );

  return (
    <div
      style={{ width, height }}
      className={cn(
        "relative",
        isSectionPreviewMember && "ring-2 ring-canvas-selection/60 rounded-lg",
      )}
    >
      <AnimatePresence>
        {showToolbar && (
          <CanvasFloatingToolbarPortal
            positionAbsoluteX={positionAbsoluteX}
            positionAbsoluteY={positionAbsoluteY}
            width={width}
            topOffset={64}
            onPointerDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              toolbarMouseDownRef.current = true;
              window.addEventListener(
                "pointerup",
                () => {
                  toolbarMouseDownRef.current = false;
                },
                { once: true, capture: true },
              );
            }}
          >
            {isEditing ? (
              <TextLabelFloatingToolbar
                visible={true}
                textColor={textColor}
                fontFamily={fontFamily}
                fontSize={fontSize}
                isBold={isBold}
                isItalic={isItalic}
                isUnderline={isUnderline}
                onTextColorChange={handleTextColorChange}
                onFontFamilyChange={handleFontFamilyChange}
                onFontSizeChange={handleFontSizeChange}
                onBoldChange={handleBoldChange}
                onItalicChange={handleItalicChange}
                onUnderlineChange={handleUnderlineChange}
                editor={editorRef.current}
                onPopoverOpenChange={handlePopoverOpenChange}
              />
            ) : (
              <ShapeFloatingToolbar
                shapeType={shapeType}
                fillColor={fillColor}
                noFill={noFill}
                strokeStyle={strokeStyle}
                onShapeTypeChange={handleShapeTypeChange}
                onColorChange={handleFillColorChange}
                onNoFillChange={handleNoFillChange}
                onStrokeStyleChange={handleStrokeStyleChange}
                onPopoverOpenChange={handlePopoverOpenChange}
              />
            )}
          </CanvasFloatingToolbarPortal>
        )}
      </AnimatePresence>

      {/* NodeResizer — both axes */}
      {selected && !isEditing && canEdit && (
        <NodeResizer
          isVisible={selected && !isEditing}
          minWidth={48}
          minHeight={48}
          onResizeStart={(_evt, params) => {
            resizeOriginRef.current = { x: params.x, y: params.y };
          }}
          onResizeEnd={(_evt, params) => {
            // Live resize steps on the lattice via the helper-lines
            // interceptor (RF's own snapToGrid is off). At end, snap as a
            // safety net and compensate position when a left/top handle
            // shifted the origin.
            const snap = (v: number) => snapToGrid(v, LATTICE);
            const w = snap(params.width);
            const h = snap(params.height);
            const origin = resizeOriginRef.current;
            const x =
              origin && params.x !== origin.x
                ? params.x + params.width - w
                : params.x;
            const y =
              origin && params.y !== origin.y
                ? params.y + params.height - h
                : params.y;

            // Single updateNode call — corrects RF state and persists to Loro.
            updateNode(id, { position: { x, y }, width: w, height: h });
            canvas.updateNodeDimensions(id, { width: w, height: h });
            canvas.updateNodePosition(id, { x, y });
          }}
        />
      )}

      {/* SVG shape fill */}
      <Shape
        type={shapeType}
        width={width}
        height={height}
        fill={fillColor}
        fillOpacity={noFill ? 0 : 1}
        stroke={effectiveStrokeColor}
        strokeWidth={effectiveStrokeWidth}
        strokeDasharray={strokeDasharray}
      />

      {/* Selection ring */}
      {selected && (
        <div className="absolute inset-0 ring-2 ring-canvas-selection/60 rounded-[1px] pointer-events-none" />
      )}

      {/* Body overlay — covers entire shape for drag / select.
          Never triggers text editing. Removed in edit mode so RF can't
          intercept pointer events while Lexical is active. */}
      {!isEditing && canEdit && (
        <div
          className="absolute inset-0 z-20 cursor-grab active:cursor-grabbing"
          onPointerDown={(e) => {
            wasSelectedOnMouseDownRef.current = selected ?? false;
            mouseDownPosRef.current = { x: e.clientX, y: e.clientY };
            if (selected) e.stopPropagation();
          }}
          onPointerUp={() => {
            mouseDownPosRef.current = null;
          }}
          onDoubleClick={(e) => {
            // Double-click on body → enter text edit mode
            e.stopPropagation();
            handleStartEditing();
          }}
        />
      )}

      {/* Text hit zone — centered area that enters edit mode on single click.
          Only active when node is selected and not already editing. */}
      {!isEditing && canEdit && selected && (
        <div
          className="absolute z-30 cursor-text flex items-center justify-center"
          style={{
            // Generous centred hit area — scales with node size
            left: "20%",
            top: "20%",
            width: "60%",
            height: "60%",
          }}
          onPointerDown={(e) => {
            e.stopPropagation();
            wasSelectedOnMouseDownRef.current = true;
            mouseDownPosRef.current = { x: e.clientX, y: e.clientY };
          }}
          onPointerUp={(e) => {
            if (!mouseDownPosRef.current) return;
            const dx = Math.abs(e.clientX - mouseDownPosRef.current.x);
            const dy = Math.abs(e.clientY - mouseDownPosRef.current.y);
            mouseDownPosRef.current = null;
            if (dx > 3 || dy > 3) return;
            handleStartEditing();
          }}
        />
      )}

      {/* Connection handles — shared with every other connectable node type. */}
      <CanvasConnectHandles />

      {/* Centred Lexical text editor, confined to the shape's inner box.
          No z-index so the overlays above can intercept while not editing.
          The column flex centres the text while it fits; the inner max-height
          clips it from the bottom once it does not (Figma's model: text is
          contained and truncated, never auto-scaled). */}
      <div
        className={cn(
          "absolute flex flex-col justify-center overflow-hidden",
          isEditing && "nodrag nowheel z-20",
        )}
        style={textInsetStyle(shapeType)}
      >
        <LexicalComposer initialConfig={initialConfig}>
          <EditorRefBridge editorRef={editorRef} />
          <RichTextPlugin
            contentEditable={
              <div
                className="max-h-full w-full overflow-hidden"
                style={
                  isEditing
                    ? undefined
                    : {
                        display: "-webkit-box",
                        WebkitBoxOrient: "vertical",
                        WebkitLineClamp: lineClamp,
                      }
                }
              >
                <ContentEditable
                  className="outline-none w-full break-words"
                  style={{
                    cursor: isEditing ? "text" : "inherit",
                    color: effectiveTextColor,
                    fontFamily,
                    fontSize,
                    fontWeight: isBold ? "bold" : "normal",
                    fontStyle: isItalic ? "italic" : "normal",
                    textDecoration: isUnderline ? "underline" : "none",
                    textAlign: "center",
                    pointerEvents: isEditing ? "auto" : "none",
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Tab") e.preventDefault();
                    e.stopPropagation();
                    if (e.key === "Escape") {
                      const rfRoot = (
                        e.target as HTMLElement
                      ).closest<HTMLElement>(".react-flow");
                      if (rfRoot) {
                        rfRoot.focus({ preventScroll: true });
                      } else {
                        (e.target as HTMLElement).blur();
                      }
                    }
                  }}
                  onFocus={() => {
                    setIsEditing(true);
                    setIsFocused(true);
                  }}
                  onBlur={() => {
                    setIsFocused(false);
                    handleBlur();
                  }}
                />
              </div>
            }
            placeholder={
              // Figma's model: an empty shape at rest shows nothing; one click
              // selects it and reveals the prompt (so the text seat is
              // findable); a second click edits. The prompt stays through the
              // edit session and leaves on click-off.
              canEdit && (selected || isEditing) ? (
                <div
                  className="absolute inset-0 flex items-center justify-center pointer-events-none text-center whitespace-nowrap"
                  style={{
                    fontFamily,
                    fontSize,
                    color: `color-mix(in oklab, ${effectiveTextColor} 55%, transparent)`,
                  }}
                >
                  {TEXT_LABEL_PLACEHOLDER}
                </div>
              ) : null
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
          <HistoryPlugin />
          <ListPlugin />
          <MarkdownShortcutPlugin transformers={TRANSFORMERS} />
          <OnChangePlugin
            onChange={handleContentChange}
            ignoreSelectionChange={true}
          />
          <InitializerPlugin
            value={content}
            isFocused={isFocused}
            onInitialized={() => setIsInitialized(true)}
          />
        </LexicalComposer>
      </div>
    </div>
  );
});
